import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { orderUploadStalled, selectOrderUpload } from './orderUploadSlice';

/**
 * How long silence is allowed to last before the screen says so.
 *
 * The task announces itself the moment it starts, so a minute without that
 * first word means it never ran — a worker that does not know the task, a dead
 * queue, or a publisher pointing at a different Redis than the broker. Once
 * events are flowing the bar is far looser: a slow file can spend minutes
 * between one batch of rows and the next.
 */
const FIRST_EVENT_MS = 60 * 1000;
const BETWEEN_EVENTS_MS = 5 * 60 * 1000;

const CHECK_EVERY_MS = 10 * 1000;

/**
 * Notices when a massive upload stops reporting.
 *
 * Without this there is no difference between a long file and a job that was
 * discarded: both look like a bar that never moves. The synchronous endpoint
 * this replaced was worse at everything except failing — it answered 400 and
 * the operator knew.
 */
export const useOrderUploadWatchdog = () => {
  const dispatch = useDispatch();
  const { phase, lastEventAt, processed } = useSelector(selectOrderUpload);

  useEffect(() => {
    if (phase !== 'running' || !lastEventAt) return undefined;

    const limit = processed > 0 ? BETWEEN_EVENTS_MS : FIRST_EVENT_MS;
    const timer = setInterval(() => {
      if (Date.now() - lastEventAt > limit) dispatch(orderUploadStalled());
    }, CHECK_EVERY_MS);

    return () => clearInterval(timer);
  }, [dispatch, phase, lastEventAt, processed]);
};
